import * as React from "react";

import { Grid } from "@mui/material";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { ChartsColorPalette, LineSeriesType } from "@mui/x-charts";
import { LineChart } from "@mui/x-charts/LineChart";
import { MakeOptional } from "@mui/x-internals/types";
import dayjs from "dayjs";
import "dayjs/locale/tr";
import { axiosCacheApi } from "../../../interceptors/axios";

// TypeScript için interface tanımlamaları
interface AreaGradientProps {
  color: string;
  id: string;
}

// API'den gelen veri yapısı
type StatisticData = {
  creationTime: string;
  isGraduationVerified: boolean;
  emailConfirmed: boolean;
}[];

// Görünüm tipi - saatlik eklendi
type ViewType = "hourly" | "daily" | "monthly";

interface ProcessedData {
  xAxisData: string[];
  cumulativeTotalCounts: number[];
  cumulativeEmailConfirmedCounts: number[];
  cumulativeGraduationVerifiedCounts: number[];
  periodCounts: Record<
    string,
    { total: number; emailConfirmed: number; graduationVerified: number }
  >;
  totalSessions: number;
  totalConfirmedEmails: number;
  totalVerifiedGraduations: number;
  growthPercentage: number;
  previousPeriodTotal: number;
  currentPeriodTotal: number;
}

// Alan için gradient oluşturan bileşen
function AreaGradient({ color, id }: AreaGradientProps) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={0.5} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>
    </defs>
  );
}

// Saatlik veri işleme fonksiyonu - YENİ
function processHourlyData(data: StatisticData): ProcessedData {
  const dayjsDates = data.map((item) => ({
    date: dayjs(item.creationTime),
    emailConfirmed: item.emailConfirmed,
    isGraduationVerified: item.isGraduationVerified,
  }));

  // Son 24 saati kapsasın
  const now = dayjs();
  const twentyFourHoursAgo = now.subtract(23, "hour").startOf("hour");

  // Saatlere göre gruplandırma
  const periodCounts: Record<
    string,
    { total: number; emailConfirmed: number; graduationVerified: number }
  > = {};

  dayjsDates.forEach((item) => {
    const hourKey = item.date.format("YYYY-MM-DD HH:00");

    if (!periodCounts[hourKey]) {
      periodCounts[hourKey] = {
        total: 0,
        emailConfirmed: 0,
        graduationVerified: 0,
      };
    }

    periodCounts[hourKey].total += 1;

    if (item.emailConfirmed) {
      periodCounts[hourKey].emailConfirmed += 1;
    }

    if (item.isGraduationVerified) {
      periodCounts[hourKey].graduationVerified += 1;
    }
  });

  const allHours: string[] = [];
  const cumulativeTotalCounts: number[] = [];
  const cumulativeEmailConfirmedCounts: number[] = [];
  const cumulativeGraduationVerifiedCounts: number[] = [];

  for (let i = 0; i < 24; i++) {
    const currentHour = twentyFourHoursAgo.add(i, "hour");
    const hourKey = currentHour.format("YYYY-MM-DD HH:00");
    const formattedHour = currentHour.format("HH:mm");

    allHours.push(formattedHour);

    // O saate kadar olan toplam kullanıcı sayısını hesapla
    const totalUntilThisHour = dayjsDates.filter(
      (item) =>
        item.date.isSame(currentHour, "hour") ||
        item.date.isBefore(currentHour, "hour")
    ).length;

    const emailConfirmedUntilThisHour = dayjsDates.filter(
      (item) =>
        (item.date.isSame(currentHour, "hour") ||
          item.date.isBefore(currentHour, "hour")) &&
        item.emailConfirmed
    ).length;

    const graduationVerifiedUntilThisHour = dayjsDates.filter(
      (item) =>
        (item.date.isSame(currentHour, "hour") ||
          item.date.isBefore(currentHour, "hour")) &&
        item.isGraduationVerified
    ).length;

    cumulativeTotalCounts.push(totalUntilThisHour);
    cumulativeEmailConfirmedCounts.push(emailConfirmedUntilThisHour);
    cumulativeGraduationVerifiedCounts.push(graduationVerifiedUntilThisHour);
  }

  // Toplam sayılar
  const totalSessions = data.length;
  const totalConfirmedEmails = data.filter(
    (item) => item.emailConfirmed
  ).length;
  const totalVerifiedGraduations = data.filter(
    (item) => item.isGraduationVerified
  ).length;

  // Saatlik büyüme hesaplama (son 2 saat karşılaştırması)
  const currentPeriodTotal =
    cumulativeTotalCounts[cumulativeTotalCounts.length - 1] || 0;
  const previousPeriodTotal =
    cumulativeTotalCounts[cumulativeTotalCounts.length - 2] || 0;

  let growthPercentage = 0;
  if (previousPeriodTotal > 0) {
    growthPercentage = Math.round(
      ((currentPeriodTotal - previousPeriodTotal) / previousPeriodTotal) * 100
    );
  }

  return {
    xAxisData: allHours,
    cumulativeTotalCounts,
    cumulativeEmailConfirmedCounts,
    cumulativeGraduationVerifiedCounts,
    periodCounts,
    totalSessions,
    totalConfirmedEmails,
    totalVerifiedGraduations,
    growthPercentage,
    previousPeriodTotal,
    currentPeriodTotal,
  };
}

// Günlük veri işleme fonksiyonu
function processDailyData(data: StatisticData): ProcessedData {
  const dayjsDates = data.map((item) => ({
    date: dayjs(item.creationTime),
    emailConfirmed: item.emailConfirmed,
    isGraduationVerified: item.isGraduationVerified,
  }));

  // Son 30 günü kapsa
  const today = dayjs();
  const thirtyDaysAgo = today.subtract(29, "day");

  // Günlere göre gruplandırma
  const periodCounts: Record<
    string,
    { total: number; emailConfirmed: number; graduationVerified: number }
  > = {};

  dayjsDates.forEach((item) => {
    const dayKey = item.date.format("YYYY-MM-DD");

    if (!periodCounts[dayKey]) {
      periodCounts[dayKey] = {
        total: 0,
        emailConfirmed: 0,
        graduationVerified: 0,
      };
    }

    periodCounts[dayKey].total += 1;

    if (item.emailConfirmed) {
      periodCounts[dayKey].emailConfirmed += 1;
    }

    if (item.isGraduationVerified) {
      periodCounts[dayKey].graduationVerified += 1;
    }
  });

  const allDays: string[] = [];
  const cumulativeTotalCounts: number[] = [];
  const cumulativeEmailConfirmedCounts: number[] = [];
  const cumulativeGraduationVerifiedCounts: number[] = [];

  for (let i = 0; i < 30; i++) {
    const currentDay = thirtyDaysAgo.add(i, "day");
    const dayKey = currentDay.format("YYYY-MM-DD");
    const formattedDay = currentDay.format("DD MMM");

    allDays.push(formattedDay);

    // O güne kadar olan toplam kullanıcı sayısını hesapla
    const totalUntilThisDay = dayjsDates.filter(
      (item) =>
        item.date.isSame(currentDay, "day") ||
        item.date.isBefore(currentDay, "day")
    ).length;

    const emailConfirmedUntilThisDay = dayjsDates.filter(
      (item) =>
        (item.date.isSame(currentDay, "day") ||
          item.date.isBefore(currentDay, "day")) &&
        item.emailConfirmed
    ).length;

    const graduationVerifiedUntilThisDay = dayjsDates.filter(
      (item) =>
        (item.date.isSame(currentDay, "day") ||
          item.date.isBefore(currentDay, "day")) &&
        item.isGraduationVerified
    ).length;

    cumulativeTotalCounts.push(totalUntilThisDay);
    cumulativeEmailConfirmedCounts.push(emailConfirmedUntilThisDay);
    cumulativeGraduationVerifiedCounts.push(graduationVerifiedUntilThisDay);
  }

  // Toplam sayılar
  const totalSessions = data.length;
  const totalConfirmedEmails = data.filter(
    (item) => item.emailConfirmed
  ).length;
  const totalVerifiedGraduations = data.filter(
    (item) => item.isGraduationVerified
  ).length;

  // Günlük büyüme hesaplama (son 2 gün karşılaştırması)
  const currentPeriodTotal =
    cumulativeTotalCounts[cumulativeTotalCounts.length - 1] || 0;
  const previousPeriodTotal =
    cumulativeTotalCounts[cumulativeTotalCounts.length - 2] || 0;

  let growthPercentage = 0;
  if (previousPeriodTotal > 0) {
    growthPercentage = Math.round(
      ((currentPeriodTotal - previousPeriodTotal) / previousPeriodTotal) * 100
    );
  }

  return {
    xAxisData: allDays,
    cumulativeTotalCounts,
    cumulativeEmailConfirmedCounts,
    cumulativeGraduationVerifiedCounts,
    periodCounts,
    totalSessions,
    totalConfirmedEmails,
    totalVerifiedGraduations,
    growthPercentage,
    previousPeriodTotal,
    currentPeriodTotal,
  };
}

// Aylık veri işleme fonksiyonu (mevcut fonksiyon)
function processMonthlyData(data: StatisticData): ProcessedData {
  const dayjsDates = data.map((item) => ({
    date: dayjs(item.creationTime),
    emailConfirmed: item.emailConfirmed,
    isGraduationVerified: item.isGraduationVerified,
  }));

  // Tarihleri aylara göre gruplandırma
  const periodCounts: Record<
    string,
    { total: number; emailConfirmed: number; graduationVerified: number }
  > = {};

  dayjsDates.forEach((item) => {
    const monthKey = item.date.format("YYYY-MM");

    if (!periodCounts[monthKey]) {
      periodCounts[monthKey] = {
        total: 0,
        emailConfirmed: 0,
        graduationVerified: 0,
      };
    }

    periodCounts[monthKey].total += 1;

    if (item.emailConfirmed) {
      periodCounts[monthKey].emailConfirmed += 1;
    }

    if (item.isGraduationVerified) {
      periodCounts[monthKey].graduationVerified += 1;
    }
  });

  // Son 12 ayı kapsayacak şekilde tüm ayları oluştur
  const today = dayjs();
  const twelveMonthsAgo = today.subtract(11, "month").startOf("month");
  const allMonths: string[] = [];

  const cumulativeTotalCounts: number[] = [];
  const cumulativeEmailConfirmedCounts: number[] = [];
  const cumulativeGraduationVerifiedCounts: number[] = [];

  for (let i = 0; i < 12; i++) {
    const currentMonth = twelveMonthsAgo.add(i, "month");
    const monthKey = currentMonth.format("YYYY-MM");
    const formattedMonth = currentMonth.format("MMM YYYY");

    allMonths.push(formattedMonth);

    // O ana kadar olan toplam kullanıcı sayısını hesapla
    const totalUntilThisMonth = dayjsDates.filter(
      (item) =>
        item.date.isSame(currentMonth.endOf("month")) ||
        item.date.isBefore(currentMonth.endOf("month"))
    ).length;

    const emailConfirmedUntilThisMonth = dayjsDates.filter(
      (item) =>
        (item.date.isSame(currentMonth.endOf("month")) ||
          item.date.isBefore(currentMonth.endOf("month"))) &&
        item.emailConfirmed
    ).length;

    const graduationVerifiedUntilThisMonth = dayjsDates.filter(
      (item) =>
        (item.date.isSame(currentMonth.endOf("month")) ||
          item.date.isBefore(currentMonth.endOf("month"))) &&
        item.isGraduationVerified
    ).length;

    cumulativeTotalCounts.push(totalUntilThisMonth);
    cumulativeEmailConfirmedCounts.push(emailConfirmedUntilThisMonth);
    cumulativeGraduationVerifiedCounts.push(graduationVerifiedUntilThisMonth);
  }

  // Toplam sayılar
  const totalSessions = data.length;
  const totalConfirmedEmails = data.filter(
    (item) => item.emailConfirmed
  ).length;
  const totalVerifiedGraduations = data.filter(
    (item) => item.isGraduationVerified
  ).length;

  // Aylık büyüme hesaplama (son 2 ay karşılaştırması)
  const currentPeriodTotal =
    cumulativeTotalCounts[cumulativeTotalCounts.length - 1] || 0;
  const previousPeriodTotal =
    cumulativeTotalCounts[cumulativeTotalCounts.length - 2] || 0;

  let growthPercentage = 0;
  if (previousPeriodTotal > 0) {
    growthPercentage = Math.round(
      ((currentPeriodTotal - previousPeriodTotal) / previousPeriodTotal) * 100
    );
  }

  return {
    xAxisData: allMonths,
    cumulativeTotalCounts,
    cumulativeEmailConfirmedCounts,
    cumulativeGraduationVerifiedCounts,
    periodCounts,
    totalSessions,
    totalConfirmedEmails,
    totalVerifiedGraduations,
    growthPercentage,
    previousPeriodTotal,
    currentPeriodTotal,
  };
}

export default function SessionsChartTotal(): JSX.Element {
  const theme = useTheme();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [statisticData, setStatisticData] =
    React.useState<StatisticData | null>(null);
  const [processedData, setProcessedData] =
    React.useState<ProcessedData | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [viewType, setViewType] = React.useState<ViewType>("monthly");

  // API'den veri çekme
  React.useEffect(() => {
    const fetchStatistics = async (): Promise<void> => {
      setLoading(true);
      try {
        const response = await axiosCacheApi.get<StatisticData>(
          "api/users/statistic"
        );
        setStatisticData(response.data);
        setLoading(false);
      } catch (err) {
        console.error("İstatistik verileri alınırken hata:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Veriler yüklenirken bir hata oluştu"
        );
        setLoading(false);
      }
    };

    fetchStatistics();
  }, []);

  // Görünüm tipine göre veri işleme
  React.useEffect(() => {
    if (statisticData) {
      let processed: ProcessedData;
      if (viewType === "hourly") {
        processed = processHourlyData(statisticData);
      } else if (viewType === "daily") {
        processed = processDailyData(statisticData);
      } else {
        processed = processMonthlyData(statisticData);
      }
      setProcessedData(processed);
    }
  }, [statisticData, viewType]);

  // Görünüm değiştirme handler'ı
  const handleViewChange = (
    _event: React.MouseEvent<HTMLElement>,
    newView: ViewType
  ) => {
    if (newView !== null) {
      setViewType(newView);
    }
  };

  // Yükleme durumu
  if (loading) {
    return (
      <Card variant="outlined" sx={{ width: "100%" }}>
        <CardContent>
          <Typography>Veriler yükleniyor...</Typography>
        </CardContent>
      </Card>
    );
  }

  // Hata durumu
  if (error) {
    return (
      <Card variant="outlined" sx={{ width: "100%" }}>
        <CardContent>
          <Typography color="error">Hata: {error}</Typography>
        </CardContent>
      </Card>
    );
  }

  // Veriler yoksa
  if (!processedData) {
    return (
      <Card variant="outlined" sx={{ width: "100%" }}>
        <CardContent>
          <Typography>Veri bulunamadı</Typography>
        </CardContent>
      </Card>
    );
  }

  // Veri serilerini oluştur (birikimli veriler)
  const generateSeries: MakeOptional<LineSeriesType, "type">[] = [
    {
      id: "sessions",
      label: "Toplam kayıt sayısı",
      showMark: true,
      curve: "linear",
      data: processedData.cumulativeTotalCounts,
      area: true,
      color: theme.palette.primary.main,
    },
    {
      id: "emailConfirmed",
      label: "Toplam e-posta onaylı",
      showMark: true,
      curve: "linear",
      data: processedData.cumulativeEmailConfirmedCounts,
      area: true,
      color: theme.palette.success.main,
    },
    {
      id: "graduationVerified",
      label: "Toplam mezuniyet doğrulaması",
      showMark: true,
      curve: "linear",
      data: processedData.cumulativeGraduationVerifiedCounts,
      area: true,
      color: theme.palette.info.main,
    },
  ];

  const colorPalate: ChartsColorPalette = [
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.info.main,
  ];

  // Görünüm tipine göre açıklama metni
  const getChartDescription = () => {
    switch (viewType) {
      case "hourly":
        return "Son 24 saat için birikimli toplam kullanıcı sayısı";
      case "daily":
        return "Son 30 gün için birikimli toplam kullanıcı sayısı";
      case "monthly":
        return "Son 12 ay için birikimli toplam kullanıcı sayısı";
      default:
        return "";
    }
  };

  // Görünüm tipine göre büyüme oranı metni
  const getGrowthRateText = () => {
    switch (viewType) {
      case "hourly":
        return "Saatlik büyüme oranı";
      case "daily":
        return "Günlük büyüme oranı";
      case "monthly":
        return "Aylık büyüme oranı";
      default:
        return "Büyüme oranı";
    }
  };

  return (
    <Card variant="outlined" sx={{ width: "100%" }}>
      <CardContent>
        <Grid container spacing={2}>
          {/* Görünüm Seçici */}
          <Grid size={{ xs: 12 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6" component="h2">
                Kullanıcı İstatistikleri
              </Typography>
              <ToggleButtonGroup
                value={viewType}
                exclusive
                onChange={handleViewChange}
                size="small"
                aria-label="görünüm tipi"
              >
                <ToggleButton value="hourly" aria-label="saatlik">
                  Saatlik
                </ToggleButton>
                <ToggleButton value="daily" aria-label="günlük">
                  Günlük
                </ToggleButton>
                <ToggleButton value="monthly" aria-label="aylık">
                  Aylık
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <Stack>
              <Stack
                direction="row"
                sx={{
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography variant="h5" component="p">
                  {processedData.totalSessions.toLocaleString("tr-TR")}
                </Typography>
                <Chip
                  size="small"
                  color={
                    processedData.growthPercentage >= 0 ? "success" : "error"
                  }
                  label={`${processedData.growthPercentage >= 0 ? "+" : ""}${
                    processedData.growthPercentage
                  }%`}
                />
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: theme.palette.primary.main,
                  }}
                />
                <Typography variant="subtitle2">Toplam kayıt sayısı</Typography>
              </Stack>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {getGrowthRateText()}
              </Typography>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <Stack>
              <Typography variant="h5" component="p">
                {processedData.totalConfirmedEmails.toLocaleString("tr-TR")}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: theme.palette.success.main,
                  }}
                />
                <Typography variant="subtitle2">
                  Toplam e-posta onaylı
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                %
                {Math.round(
                  (processedData.totalConfirmedEmails /
                    processedData.totalSessions) *
                    100
                )}{" "}
                onay oranı
              </Typography>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <Stack>
              <Typography variant="h5" component="p">
                {processedData.totalVerifiedGraduations.toLocaleString("tr-TR")}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: theme.palette.info.main,
                  }}
                />
                <Typography variant="subtitle2">
                  Toplam mezuniyet doğrulaması
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                %
                {Math.round(
                  (processedData.totalVerifiedGraduations /
                    processedData.totalSessions) *
                    100
                )}{" "}
                doğrulama oranı
              </Typography>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {getChartDescription()}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <LineChart
              colors={colorPalate}
              xAxis={[
                {
                  scaleType: "point",
                  data: processedData.xAxisData,
                  tickInterval:
                    viewType === "hourly"
                      ? (index: number) => index % 2 === 0 // Her 2 saatte bir göster
                      : viewType === "daily"
                      ? (index: number) => index % 3 === 0 // Her 3 günde bir göster
                      : (index: number) => index % 2 === 0, // Her 2 ayda bir göster
                },
              ]}
              series={generateSeries}
              height={377}
              grid={{ horizontal: true }}
              sx={{
                "& .MuiAreaElement-series-sessions": {
                  fill: "url('#sessions')",
                },
                "& .MuiAreaElement-series-emailConfirmed": {
                  fill: "url('#emailConfirmed')",
                },
                "& .MuiAreaElement-series-graduationVerified": {
                  fill: "url('#graduationVerified')",
                },
              }}
              slotProps={{
                legend: {
                  position: {
                    vertical: "top",
                    horizontal: "end",
                  },
                },
              }}
            >
              <AreaGradient color={theme.palette.primary.main} id="sessions" />
              <AreaGradient
                color={theme.palette.success.main}
                id="emailConfirmed"
              />
              <AreaGradient
                color={theme.palette.info.main}
                id="graduationVerified"
              />
            </LineChart>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
